import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Loader2, Wrench, Smartphone } from 'lucide-react';

export default function HandymanLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('handyman_token');
      const user = localStorage.getItem('handyman_user');
      
      if (token && user) {
        try {
          // Verify token is still valid
          const userData = JSON.parse(user);
          if (userData.role === 'handyman') {
            onLogin(userData, token);
            navigate('/handyman');
            return;
          }
        } catch (error) {
          // Invalid session, clear it
          localStorage.removeItem('handyman_token');
          localStorage.removeItem('handyman_user');
        }
      }
      setCheckingSession(false);
    };

    checkExistingSession();
  }, [onLogin, navigate]);

  // PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password
      });

      const { token, user } = response.data;

      if (user.role !== 'handyman') {
        toast.error('Access denied. This app is for handymen only.');
        setLoading(false);
        return;
      }

      // Store session persistently
      localStorage.setItem('handyman_token', token);
      localStorage.setItem('handyman_user', JSON.stringify(user));

      toast.success(`Welcome back, ${user.name}!`);
      onLogin(user, token);
      navigate('/handyman');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex flex-col items-center justify-center p-4 safe-area-inset">
      {/* Logo Section */}
      <div className="mb-8 text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_1dafeb02-9ec2-4436-8d6b-af3019fabee1/artifacts/je75u1q2_White%20logo%20thick%20copy.jpg" 
          alt="Redefyne Logo" 
          className="w-48 h-auto mx-auto mb-4 rounded-2xl shadow-2xl"
        />
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Handyman Portal
        </h1>
        <p className="text-teal-100 text-sm">
          Manage your jobs, schedules & timesheets
        </p>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-sm backdrop-blur-lg bg-white/95 shadow-2xl border-0 rounded-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Wrench className="h-8 w-8 text-teal-600" />
          </div>
          <CardTitle className="text-xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 text-base"
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 text-base"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base bg-teal-600 hover:bg-teal-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Install App Button */}
          {showInstallPrompt && (
            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                className="w-full h-12 text-base"
                onClick={handleInstallClick}
              >
                <Smartphone className="mr-2 h-5 w-5" />
                Install App
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Add to home screen for quick access
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* iOS Install Instructions */}
      <div className="mt-6 text-center text-white/80 text-sm max-w-xs">
        <p className="mb-1">📱 <strong>Install on iPhone:</strong></p>
        <p className="text-xs">Tap the Share button, then "Add to Home Screen"</p>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-white/80 text-xs space-y-1">
        <p className="font-medium">2301 S Stearman Dr, Chandler, AZ 85286</p>
        <p>+1 602-715-1313 | support@redefyneaz.com</p>
        <p className="text-white/60 mt-2">&copy; 2025 Redefyne Interior & Exterior</p>
      </div>
    </div>
  );
}
